
import {finalize} from 'rxjs/operators';
import { each, find, remove } from 'lodash';

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Dossier } from './dossier';
import { DossierService } from './dossier.service';
import { DossierModel } from '../dossier-model/dossier-model';
import { RecordLink } from '../record-link/record-link';
import { RecordLinkService } from '../record-link/record-link.service';
import { Record } from '../record/record';
import { RecordSearchDialogComponent } from '../record/record-search-dialog.component';
import { RecordService } from '../record/record.service';
import { NotifierService, DialogService } from '../ui';
import { Link } from './link';
import { TreeItem, DossierTree, RecordItem, TreeItemWithRecord, SimpleItemWithGroups, GroupItem, DataItem, GroupMovedEvent } from './tree';
import { LinkGroup } from '../link-group/link-group';
import { DataLink } from '../data-link/data-link';
import { DataLinkService } from '../data-link/data-link.service';
import { LinkGroupService } from '../link-group/link-group.service';

type ViewMode = 'new' | 'edit';

@Component({
  templateUrl: './dossier-edit.component.html',
})
export class DossierEditComponent implements OnInit {
  
  dossier: Dossier
  dossierModel: DossierModel
  dossierTree: DossierTree

  mode: ViewMode
  title: string

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dossierService: DossierService,
    private recordLinkService: RecordLinkService,
    private dataLinkService: DataLinkService,
    private recordService: RecordService,
    private linkGroupService: LinkGroupService,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) { }

  ngOnInit() {

    this.mode = this.route.routeConfig.data.mode;
    this.title = this.route.routeConfig.data.title;

    // Get the data
    this.notifier.startProgress();

    this.route.data.subscribe((data) => {

      this.dossierModel = data.dossierModel;

      switch (this.mode) {
        case 'new':
          {
            this.dossier = this.createEmptyDossier();

            this.dossierTree = new DossierTree(this.dossier, this.dossierModel);

            this.notifier.endProgress();
          }
          break;

        case 'edit':
          {
            let dossier: Dossier = data.dossier;

            this.initState(data.dossier);
          }
          break;
      }
    });
  }

  private initState(dossier: Dossier) {
    // Attach models on each link
    this.attachModelToLinks(dossier.links);

    // Set the dossier
    this.dossier = dossier;
    this.dossierTree = new DossierTree(this.dossier, this.dossierModel);

    // Select the root link
    this.loadRecordLink(this.dossierTree.rootItem);
  }

  private createEmptyDossier(): Dossier {
    let rootTarget = find(this.dossierModel.targets, target => target.link.is_root);
    let rootLink = this.createEmptyRecordLink(rootTarget, null);

    let dossier = new Dossier();
    dossier.links = [rootLink];

    return dossier;
  }

  private createEmptyRecordLink(target: any, group: LinkGroup): RecordLink {
    let linkModel = target.link;

    // Init the link
    let link = new RecordLink();

    link.name = target.id;
    link.group_id = group ? group.id : null;
    link.model = linkModel;

    link.record = new Record();

    return link;
  }

  private createEmptyDataLink(target: any): DataLink {
    let linkModel = target.link;

    // Init the link
    let link = new DataLink();

    link.name = target.id;
    link.model = linkModel;

    return link;
  }

  private attachModelToLinks(links: Link[]) {
    each(links, link => {
      this.attachModelToLink(link);
    });
  }
  
  private attachModelToLink(link: Link) {
    link.model = this.dossierModel.data[link.name];
  }

  /**
   * Apply a record change to all links sharing that record
   */
  private replaceRecord(record: Record) {
    each(this.dossier.links, l => {
      if(l.isRecordLink) {
        let link = l as RecordLink;

        if(link.record && link.record.id === record.id) {
          link.record = record;
        }
      }
    });
  }

  get selectedRecordLink(): RecordLink {
    let item = this.dossierTree.selectedItem as any;
    if (item && (item.type === 'ROOT' || item.type === 'RECORD')) {
      let recordItem = item as TreeItemWithRecord;
      
      if(recordItem.link && recordItem.link.record) {
        return recordItem.link;
      }
    }

    return null;
  }

  get selectedDataLink(): DataLink {
    let item = this.dossierTree.selectedItem;
    if (item && (item.type === 'DATA')) {
      let dataItem = item as DataItem;

      if (dataItem.link && dataItem.link) {
        return dataItem.link;
      }
    }

    return null;
  }

  get selectedLinkGroup(): LinkGroup {
    let item = this.dossierTree.selectedItem;
    if (item && (item.type === 'GROUP')) {
      let groupItem = item as GroupItem;

      if (groupItem.group && groupItem.group) {
        return groupItem.group;
      }
    }

    return null;
  }

  save() {
    if(this.selectedRecordLink) {
      this.saveRecordLink(this.selectedRecordLink);
    }
    else if(this.selectedDataLink) {
      this.saveDataLink(this.selectedDataLink);
    }
    else if (this.selectedLinkGroup) {
      this.saveLinkGroup(this.selectedLinkGroup);
    }
  }

  saveRecordLink(recordLink: RecordLink) {
    let parent = this.dossierTree.selectedItem.parent;

    if (!recordLink || !recordLink.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    let handleNewLink = (link: RecordLink) => {
      this.attachModelToLink(link);
      this.replaceRecord(link.record);
      this.dossier.links.push(link);

      let item = new RecordItem(link, parent, this.dossierTree);
      parent.addChild(item);
      this.selectItem(item);
    }

    switch (this.mode) {
      case 'new':
        {
          this.dossierService
            .create(this.dossier).pipe(
            finalize(onComplete))
            .subscribe((dossier) => {
              this.dossier = dossier;

              this.notifier.success('Dossier créé avec succès');

              // Got to edit screen
              this.router.navigate(['dossiers', dossier.id]);
            });
        }
        break;

      case 'edit':

        // The link is to be updated
        if (recordLink.id) {
          this.recordLinkService
            .update(recordLink.id, recordLink).pipe(
            finalize(onComplete))
            .subscribe((link) => {

              this.attachModelToLink(link);
              this.replaceRecord(link.record);
              recordLink.replaceWith(link);

              // Get the new name for the dossier
              if (recordLink.model.is_root) {
                this.dossier.name = recordLink.record.name;
              }

              this.notifier.success('Enregistrement modifié avec succès');
            });
        }

        // The link is to be created from a existing record
        else if (recordLink.record.id) {
          this.recordLinkService
            .linkExistingRecord(this.dossier.id, recordLink.record.id, recordLink).pipe(
            finalize(onComplete))
            .subscribe((link) => {
              handleNewLink(link);

              this.notifier.success('Enregistrement attaché avec succès');
            });
        }

        // The link is to be created from a new record
        else if (!recordLink.record.id) {
          this.recordLinkService
            .linkNewRecord(this.dossier.id, recordLink).pipe(
            finalize(onComplete))
            .subscribe((link) => {
              handleNewLink(link);

              this.notifier.success('Enregistrement ajouté avec succès');
            });
        }
        break;
    }
  }

  saveDataLink(dataLink: DataLink) {
    let parent = this.dossierTree.selectedItem.parent;

    if (!dataLink || !dataLink.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    // The link is to be updated
    if (dataLink.id) {
      this.dataLinkService
        .update(dataLink.id, dataLink).pipe(
        finalize(onComplete))
        .subscribe((link) => {

          this.attachModelToLink(link);
          dataLink.replaceWith(link);

          this.notifier.success('Donnée modifiée avec succès');
        });
    }

    // The link is to be created
    else {
      this.dataLinkService
        .create(this.dossier.id, dataLink).pipe(
        finalize(onComplete))
        .subscribe((link) => {
          this.attachModelToLink(link);
          this.dossier.links.push(link);

          let item = new DataItem(link, parent, this.dossierTree);
          parent.addChild(item);
          this.selectItem(item);

          this.notifier.success('Donnée ajoutée avec succès');
        });
    }
  }

  saveLinkGroup(linkGroup: LinkGroup) {
    let parent = this.dossierTree.selectedItem.parent;

    if (!linkGroup || !linkGroup.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    // The link group is to be updated
    if (linkGroup.id) {
      this.linkGroupService
        .update(this.dossier.id, linkGroup.id, linkGroup).pipe(
        finalize(onComplete))
        .subscribe((group) => {

          linkGroup.replaceWith(group);

          this.notifier.success('Groupe modifié avec succès');
        });
    }

    // The link group is to be created
    else {
      this.linkGroupService
        .create(this.dossier.id, linkGroup).pipe(
        finalize(onComplete))
        .subscribe((group) => {

          this.dossier.link_groups.push(group);

          let item = new GroupItem(group, parent, this.dossierTree);
          (parent as SimpleItemWithGroups).addGroup(item);
          this.selectItem(item);

          this.notifier.success('Groupe ajouté avec succès');
        });
    }
  }

  cleanupNewItem() {
    let item = this.dossierTree.selectedItem;

    if(item && item.isNew) {

      if(item.parent) {
        item.parent.removeChild(item);
      }
    }
  }

  cancel() { 
    if (this.dossierTree.selectedItem === this.dossierTree.rootItem) {
      this.router.navigateByUrl('/dossiers');
    }
    else {
      this.selectRootItem();
    }
  }

  selectItem(item: TreeItem) {

    if(!item) {
      return;
    }

    if(item === this.dossierTree.selectedItem) {
      return;
    }
    
    if (!item.canSelect) {
      return;
    }

    this.cleanupNewItem();

    this.dossierTree.selectedItem = item;
    
    if (item.type === 'ROOT' || item.type === 'RECORD') {
      this.loadRecordLink(item as any);
    }
  }

  selectRootItem() {
    this.selectItem(this.dossierTree.rootItem);
  }

  loadRecordLink(item: TreeItemWithRecord) {
    
    if (this.notifier.currentProgress === 0) {
      this.notifier.startProgress();
    }
    else {
      this.notifier.updateProgress(50);
    }

    this.recordService
      .recordFromLink(this.dossier.id, item.link.id).pipe(
      finalize(() => {
        this.notifier.endProgress();
      }))
      .subscribe((link) => {
        this.attachModelToLink(link);
        item.link.replaceWith(link);
      });
  }

  add(item: TreeItem) {
    let parent: TreeItem = null;
    let group: LinkGroup = null;
    let target: any = null;

    this.cleanupNewItem();

    switch (item.type) {
      case 'SIMPLE_WITH_GROUPS':
        let simpleItemWithGroup = item as SimpleItemWithGroups;
        let selectedGroup = simpleItemWithGroup.selectedGroup;
        
        group = selectedGroup.group;
        target = simpleItemWithGroup.target;
        parent = selectedGroup;
        break;

      case 'SIMPLE':
        let simpleItem = item as SimpleItemWithGroups;
        target = simpleItem.target;
        parent = simpleItem;
        break;

      case 'GROUP':
        let groupItem = item as GroupItem;
        group = groupItem.group;
        target = (groupItem.parent as SimpleItemWithGroups).target;
        parent = groupItem;
        break;
    
      default:
        break;
    }

    let linkType = target.link.link_type;

    if(linkType === 'RecordLink') {
      let link = this.createEmptyRecordLink(target, group);

      let newItem = new RecordItem(link, parent, this.dossierTree);

      parent.addChild(newItem);
      this.dossierTree.selectedItem = newItem;
    }
    else if(linkType === 'DataLink') {
      let link = this.createEmptyDataLink(target);

      let newItem = new DataItem(link, parent, this.dossierTree);

      parent.addChild(newItem);
      this.dossierTree.selectedItem = newItem;
    }
  }
  
  addGroup(item: TreeItem) {
    let simpleItemWithGroup = item as SimpleItemWithGroups;

    this.cleanupNewItem();
    
    simpleItemWithGroup.expand();
    
    let group = new LinkGroup({
      link_type: simpleItemWithGroup.target.link.link_type,
      link_name: simpleItemWithGroup.target.id,
      order: simpleItemWithGroup.nextOrder
    });
    
    let newItem = new GroupItem(group, simpleItemWithGroup, this.dossierTree);

    simpleItemWithGroup.addGroup(newItem);
    this.dossierTree.selectedItem = newItem;
  }

  // #region Delete

  delete(item: TreeItem) {
    switch (item.type) {
      case 'RECORD':
        this.deleteRecordItem(item as RecordItem);
        break;

      case 'DATA':
        this.deleteDataItem(item as DataItem);
        break;
    
      default:
        break;
    }
  }

  deleteRecordItem(item: RecordItem) {
    let link = item.link;

    this.dialogService
      .confirmDelete('Voulez-vous supprimer ce lien d\'enregistrement ?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.recordLinkService
          .delete(link.id).pipe(
          finalize(() => {
            this.notifier.endProgress();
          }))
          .subscribe((id) => {
            this.notifier.success('Lien d\'enregistrement supprimé avec succès');

            remove(this.dossier.links, link => link.id === id);

            this.deleteItemFromDossierTree(item);
          });
      });

    return false;
  }

  deleteDataItem(item: DataItem) {
    let link = item.link;

    this.dialogService
      .confirmDelete('Voulez-vous supprimer cette donnée ?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.dataLinkService
          .delete(link.id).pipe(
          finalize(() => {
            this.notifier.endProgress();
          }))
          .subscribe((id) => {
            this.notifier.success('Donnée supprimée avec succès');

            remove(this.dossier.links, link => link.id === id);

            this.deleteItemFromDossierTree(item);
          });
      });

    return false;
  }

  // #endregion

  // #region Delete Group

  deleteGroup(item: TreeItem) {
    switch (item.type) {
      case 'GROUP':
        this.deleteGroupItem(item as GroupItem);
        break;

      case 'SIMPLE_WITH_GROUPS':
        this.deleteGroupItem((item as SimpleItemWithGroups).selectedGroup);
        break;

      default:
        break;
    }
  }

  deleteGroupItem(item: GroupItem) {
    let group = item.group;

    this.dialogService
      .confirmDelete('Voulez-vous supprimer ce groupe ?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.linkGroupService
          .deleteLinkGroupAndLinks(this.dossier.id, group.id).pipe(
          finalize(() => {
            this.notifier.endProgress();
          }))
          .subscribe((id) => {
            this.notifier.success('Groupe supprimé avec succès');

            remove(this.dossier.link_groups, group => group.id === id);

            this.deleteItemFromDossierTree(item);
          });
      });

    return false;
  }

  // #endregion

  deleteItemFromDossierTree(item: TreeItem) {
    item.parent.removeChild(item);

    if (this.dossierTree.selectedItem === item) {
      this.selectRootItem();
    }
  }

  // #region Duplicate Group

  duplicateGroup(item: TreeItem) {
    switch (item.type) {
      case 'GROUP':
        this.duplicateGroupItem(item as GroupItem);
        break;

      case 'SIMPLE_WITH_GROUPS':
        this.duplicateGroupItem((item as SimpleItemWithGroups).selectedGroup);
        break;

      default:
        break;
    }
  }

  duplicateGroupItem(item: GroupItem) {
    let group = item.group;

    this.notifier.startProgress();

    this.linkGroupService
      .duplicateLinkGroupAndLinks(this.dossier.id, group.id)
      .subscribe((newGroup) => {
        this.notifier.updateProgress(50);

        // Load new links
        this.dossierService.linksForGroup(this.dossier.id, newGroup).pipe(
        finalize(() => {
          this.notifier.endProgress();
        }))
        .subscribe((links) => {

          // Add links/group to dossier
          each(links, l => {
            this.attachModelToLink(l);
            this.dossier.links.push(l);
          })

          this.dossier.link_groups.push(newGroup);

          // Add group item to the tree
          let simpleItemWithGroup = item.parent as SimpleItemWithGroups;

          let newItem = new GroupItem(newGroup, simpleItemWithGroup, this.dossierTree);
          simpleItemWithGroup.addGroup(newItem);
          simpleItemWithGroup.expand();

          this.selectItem(newItem);

          this.notifier.success('Groupe dupliqué avec succès');
        });

      });
      
  }

  // #endregion

  moveGroup(event: GroupMovedEvent) {
    let item = event.item;
    let group = item.group;
    let oldOrder = group.order;
    let newOrder = event.newOrder;
    let otherGroup = find(this.dossier.link_groups, g => g.order === newOrder);

    let groupInput = new LinkGroup(group);
    groupInput.order = newOrder;

    let otherGroupInput = new LinkGroup(otherGroup);
    otherGroupInput.order = oldOrder;

    if(!groupInput.valid || !otherGroupInput.valid) {
      return;
    }

    this.notifier.startProgress();

    // Change order for the group
    this.linkGroupService
    .update(this.dossier.id, group.id, groupInput)
    .subscribe((updatedGroup) => {
      this.notifier.updateProgress(50);
      
      // Change order for the other group
      this.linkGroupService
      .update(this.dossier.id, otherGroup.id, otherGroupInput).pipe(
      finalize(() => {
        this.notifier.endProgress();
      }))
      .subscribe((updatedOtherGroup) => {

        group.replaceWith(updatedGroup);
        otherGroup.replaceWith(updatedOtherGroup);

        (item.parent as SimpleItemWithGroups).reorderGroups();

        this.notifier.success('Groupe déplacé avec succès');
      });

    });
  }

  lookupRecord(event: Event) {
    this.dialogService.show({
      component: RecordSearchDialogComponent,
      props: {

        hideId: this.dossierTree.rootItem.link.record.id,

        onClose: () => {
          this.dialogService.hide();
        },

        onSelect: (record: Record) => {
          this.dialogService.hide();

          this.recordService
            .record(record.id).pipe(
            finalize(() => {
              this.notifier.endProgress();
            }))
            .subscribe((record) => {
              this.selectedRecordLink.record = record;
            });
        }
      }
    });

    event.preventDefault();
  }

  get slVersionMismatch() {
    if (!this.dossier.slEntity) {
      return false;
    }

    return this.dossier.slEntity.version > this.dossier.sl_entity_version;
  }

  slUpdate(event: Event) {
    event.preventDefault();

    this.dialogService
      .confirm({
        title: 'Mise-à-jour',
        message: `Voulez-vous importer la dernière version ?`,
        confirm_button_text: 'Importer',
        confirm_button_class: 'btn-primary',
      })
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.dossierService
          .updateSLEntity(this.dossier.id, {
            sl_entity_version: this.dossier.slEntity.version,
          })
          .pipe(
          finalize(() => {
            this.notifier.endProgress();
          }))
          .subscribe((dossier) => {
            this.notifier.success('Données importées avec succès');

            this.initState(dossier);
          });
      });
  }
}
